import * as React from "react";
import styled from "styled-components";
import { styles, theme } from "../../lib/styles";
import { Icon, Text } from "../";
import DropdownItem from "./camera-dropdown-item";
import { IUserCameraAssociations } from "src/types/store/users";
import * as _ from "lodash";
import { IAuthStore } from "src/types/store/auth";

interface IDropdownProps {
  kind?: string;
  selected: number;
  style?: React.CSSProperties;
  onSelect: (data: IUserCameraAssociations) => void;
  userCameras: IUserCameraAssociations[];
  isOverlayActive?: boolean;
  auth: IAuthStore;
}

interface IDropdownState {
  open: boolean;
}
interface IDropdownHeaderWrapper {
  userCameras: IUserCameraAssociations[];
  open?: boolean;
  isOverlayActive?: boolean;
}

interface IUserCamerasBySite {
  [key: string]: IUserCameraAssociations[];
}

class Dropdown extends React.Component<IDropdownProps, IDropdownState> {
  protected wrapper: HTMLDivElement;
  protected boundOnMousedown: () => void;

  constructor(props: IDropdownProps) {
    super(props);
    this.boundOnMousedown = this.handleClickOutside.bind(this);
    this.state = { open: false };
  }

  public UNSAFE_componentWillMount() {
    document.addEventListener(
      "mousedown", this.boundOnMousedown, false
    );
  }

  public componentWillUnmount() {
    document.removeEventListener(
      "mousedown", this.boundOnMousedown, false
    );
  }

  public render() {
    const {
      kind,
      style,
      selected,
      userCameras,
      isOverlayActive,
      auth
    } = this.props;

    const { open } = this.state;

    const ref = (element: HTMLDivElement) => {
      this.wrapper = element;
    };

    const userCamerasBySite: IUserCamerasBySite = {};

    this.props.userCameras.forEach(cameraLocation => {
      const customerSiteName = `${cameraLocation.site.customer.name} - ${cameraLocation.site.name}`;

      if (userCamerasBySite[customerSiteName]) {
        userCamerasBySite[customerSiteName].push(cameraLocation);
      } else {
        userCamerasBySite[customerSiteName] = [cameraLocation];
      }
    });

    const selectedSiteName =
      selected !== -1 && userCameras
        ? `${userCameras[selected].site.name} `
        : "";

    const selectedCameraName =
      selected !== -1 && userCameras
        ? `${userCameras[selected].name} `
        : "";

    return (
      <div ref={ref}>
        <DropdownWrapper style={style} kind={kind || "default"}>
          <DropdownHeaderWrapper
            userCameras={userCameras}
            onClick={() => this.handleClick()}
            open={open}
            isOverlayActive={isOverlayActive}
          >
            <ChevronWrapper>
              <Icon
                icon="camera"
                size={25}
                style={{ transform: "translate(-30px, 0px)" }}
              />
            </ChevronWrapper>
            <Text
              style={{
                color: `${styles.fadedTextColor}`,
                fontSize: "10px",
                fontWeight: 400,
                letterSpacing: "2px",
                textTransform: "uppercase"
              }}
            >
              {selectedSiteName}
            </Text>
            <Text
              style={{
                overflow: "hidden",
                paddingTop: "1px",
                whiteSpace: "nowrap"
              }}
            >
              {selectedCameraName}
            </Text>
            {userCameras.length > 1 && (
              <ChevronWrapper style={{ right: 0 }}>
                <Icon
                  icon={open ? "chevron-up" : "chevron-down"}
                  style={{ transform: "translateY(2px)" }}
                  size={13}
                />
              </ChevronWrapper>
            )}
          </DropdownHeaderWrapper>
          {open && (
            <DropDownItemsWrapper
              isOverlayActive={isOverlayActive}
              userCameras={userCameras}
            >
              {Object.keys(userCamerasBySite)
                .sort((a, b) => a.localeCompare(b))
                .map(key => {
                  return (
                    <SiteGroup key={key}>
                      <p>{key}</p>
                      {userCamerasBySite[key]
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .map((item, index) => {
                          return (
                            <DropdownItem
                              isSelected={userCameras[selected]?.id === item?.id}
                              onSelect={() => this.handleSelect(item)}
                              key={`dropdown-${index}`}
                              text={ auth.admin && item.hardware_ref 
                                ? `${item.name}: ${item.hardware_ref}` 
                                : `${item.name}`}
                            />
                          );
                        })}
                    </SiteGroup>
                  );
                })}
            </DropDownItemsWrapper>
          )}
        </DropdownWrapper>
      </div>
    );
  }

  private handleSelect(item: IUserCameraAssociations) {
    this.setState({ open: false });
    this.props.onSelect(item);
  }

  private handleClick() {
    const { open } = this.state;
    const { userCameras } = this.props;

    if (userCameras.length > 1) {
      this.setState({ open: !open });
    }
  }

  private handleClickOutside(e: Event) {
    if (this.wrapper && this.wrapper.contains(e.target as Node | null)) {
      return;
    } else {
      if (this.wrapper) {
        this.setState({ open: false });
      }
    }
  }
}

const SiteGroup = styled.div`
  p {
    font-size: 10px;
    font-weight: 400;
    letter-spacing: 2px;
    text-transform: uppercase;
    padding: 10px;
    padding-bottom: 5px;
    margin: 0px 15px;
    color: ${styles.fadedTextColor};
  }
  margin-top: 0;
  transition: color 0.2s ease-in-out;
`;

const textColor = theme.variants(
  "mode", "kind", { default: { normal: styles.secondaryTextColor } }
);

const DropdownWrapper = styled.div<{kind?: string}>`
  position: relative;
  max-width: 260px;
  display: flex;
  flex-direction: row;
  padding-left: 20px;

  @media (max-width: ${styles.mobileBreakPoint}) {
    padding-left: 0px;

    &:after {
      display: block;
      position: absolute;
      height: 35px;
      right: -15px;
      width: 1px;
      background: rgba(255, 255, 255, 0.2);
      content: "";
      top: 50%;
      transform: translateY(-50%);
    }
  }
`;

const ChevronWrapper = styled.div`
  position: absolute;
  bottom: 0px;
`;

const DropDownItemsWrapper = styled.div<IDropdownHeaderWrapper>`
  position: absolute;
  width: 100%;
  top: 100%;
  background: ${styles.secondaryDarkColor};
  z-index: 9;
  border-radius: 0;
  padding-top: 25px;
  padding-bottom: 10px;
  border-right: 3px solid ${styles.primaryDarkColor};
  border-bottom: 3px solid ${styles.primaryDarkColor};
  max-height: 500px;
  overflow-y: auto;

  @media (max-width: 500px) {
    max-height: 300px;
    width: 90vw;
    left: 0;
  }
`;

const DropdownHeaderWrapper = styled.div<IDropdownHeaderWrapper>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding-left: 30px;
  width: 100%;
  color: ${textColor};
  justify-content: space-between;
  cursor: ${props => (props.userCameras.length > 1 ? "pointer" : "inherit")};
  span {
    max-width: 165px;
  }
  @media (min-width: ${styles.mobileBreakPoint}) {
    span {
      max-width: 190px;
    }
  }

  div svg:hover {
    fill: ${styles.secondaryTextColor};
    cursor: ${props =>
    props.userCameras.length > 1 ? "pointer" : "default"};
  }
  :hover {
    color: ${styles.primaryAccentColor};
  }
`;

export default Dropdown;
